import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Avatar,
  Chip,
  Collapse,
  Badge,
} from '@mui/material';
import {
  Dashboard,
  Article,
  Add,
  Schedule,
  Analytics,
  Settings,
  Logout,
  TrendingUp,
  Share,
  VoiceChat,
  Web,
  VideoLibrary,
  Translate,
  Accessibility,
  BrandingWatermark,
  Event,
  AdminPanelSettings,
  People,
  Security,
  MonetizationOn,
  ExpandLess,
  ExpandMore,
  Timeline,
  Link,
  Warning,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { AurieqLogo } from '../Brand/AurieqLogo';
import { logoutUser } from '../../store/slices/authSlice';

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  roles?: string[];
  badge?: string | number;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Create Post', icon: <Add />, path: '/create-post' },
  { text: 'Posts', icon: <Article />, path: '/posts' },
  { text: 'Trending', icon: <TrendingUp />, path: '/trending' },
  { text: 'Schedule', icon: <Schedule />, path: '/schedule' },
  { text: 'Analytics', icon: <Analytics />, path: '/analytics' },
  { 
    text: 'AI Tools', 
    icon: <VoiceChat />, 
    path: '#',
    children: [
      { text: 'Voice to Text', icon: <VoiceChat />, path: '/voice' },
      { text: 'Website Analysis', icon: <Web />, path: '/website-analysis' },
      { text: 'Video Scripts', icon: <VideoLibrary />, path: '/video-scripts' },
      { text: 'Translation', icon: <Translate />, path: '/translation' },
      { text: 'Accessibility', icon: <Accessibility />, path: '/accessibility' },
      { text: 'Brand Consistency', icon: <BrandingWatermark />, path: '/brand' },
      { text: 'Event Triggers', icon: <Event />, path: '/events' },
    ]
  },
  { text: 'Social Integrations', icon: <Link />, path: '/social-integrations' },
  { text: 'Integrations', icon: <Share />, path: '/integrations' },
  { text: 'Settings', icon: <Settings />, path: '/settings' },
];

const adminMenuItems: MenuItem[] = [
  { 
    text: 'Admin Panel', 
    icon: <AdminPanelSettings />, 
    path: '#',
    roles: ['admin', 'superuser'],
    children: [
      { text: 'Dashboard', icon: <Dashboard />, path: '/admin' },
      { text: 'User Management', icon: <People />, path: '/admin/users' },
      { text: 'Roles & Permissions', icon: <Security />, path: '/admin/roles' },
      { text: 'Subscription Plans', icon: <MonetizationOn />, path: '/admin/plans' },
      { text: 'System Analytics', icon: <Timeline />, path: '/admin/analytics' },
      { text: 'Emergency Security', icon: <Warning />, path: '/admin/emergency' },
    ]
  },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [expandedMenus, setExpandedMenus] = React.useState<string[]>([]);

  // Check if user has admin privileges
  const isAdmin = user?.is_superuser || 
                  user?.roles?.includes('admin') || 
                  user?.roles?.includes('superuser');

  const handleNavigation = (path: string) => {
    if (path !== '#') {
      navigate(path);
    }
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/login', { replace: true });
  };

  const toggleMenu = (menuText: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuText) 
        ? prev.filter(item => item !== menuText)
        : [...prev, menuText]
    );
  };

  const hasPermission = (item: MenuItem): boolean => {
    if (!item.roles) return true;
    if (!user) return false;
    
    return item.roles.some(role => 
      user.is_superuser || 
      user.roles?.includes(role)
    );
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
    if (!hasPermission(item)) return null;

    const isSelected = location.pathname === item.path;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.text);

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding>
          <ListItemButton
            selected={isSelected && !hasChildren}
            onClick={() => {
              if (hasChildren) {
                toggleMenu(item.text);
              } else {
                handleNavigation(item.path);
              }
            }}
            sx={{
              mx: 1,
              borderRadius: 1,
              pl: 2 + level * 2,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isSelected && !hasChildren ? 'white' : 'inherit',
                minWidth: 36,
              }}
            >
              {item.badge ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontSize: level > 0 ? '0.875rem' : '1rem'
              }}
            />
            {hasChildren && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map(child => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand */}
      <Toolbar sx={{ px: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <AurieqLogo variant="mark" height={28} to="/dashboard" />
          {isAdmin && (
            <Chip 
              label="Admin" 
              size="small" 
              color="error" 
              sx={{ ml: 'auto', fontSize: '0.7rem' }} 
            />
          )}
        </Box>
      </Toolbar>

      <Divider />

      {/* User Info */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
          {(user?.full_name || user?.username)?.charAt(0).toUpperCase() || 'U'}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.full_name || user?.username || 'User'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email || 'user@example.com'}
          </Typography>
          {user?.subscription && (
            <Chip 
              label={user.subscription.plan} 
              size="small" 
              color={user.subscription.plan === 'enterprise' ? 'success' : 
                     user.subscription.plan === 'pro' ? 'primary' : 'default'}
              sx={{ mt: 0.5, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {menuItems.map(item => renderMenuItem(item))}
        
        {/* Admin Menu Items */}
        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            {adminMenuItems.map(item => renderMenuItem(item))}
          </>
        )}
      </List>

      <Divider />

      {/* Bottom Actions */}
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ mx: 1, borderRadius: 1 }}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
};

export default Sidebar; 